import React from 'react'

import {
  Icon,
  Modal,
  ModalContent,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { BalanceDetailItem } from 'models/BalanceDetails'
import moment from 'moment'

import { ButtonRounded } from 'components/button'
import IconContainer from 'components/IconContainer/IconContainer'
import { EnhancedTable } from 'components/Table'

import { formatMoney } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

type Props = {
  isOpen: boolean
  handleClose: () => void
  /** Rótulo do grupo, ex.: "456x Notificações extras". */
  title: string
  /** Os itens do grupo, já montados pela API no payload do extrato. */
  items: NonNullable<BalanceDetailItem['items']>
  /** Rótulo da coluna de data, que nomeia o evento do tipo ("Data da emissão", "Data do envio"). */
  dateLabel?: string
  /** Abre o detalhe de uma expense específica. */
  onOpenExpenseDetail: (uuid: string) => void
}

const formatDate = (date?: string) =>
  date ? moment(date.slice(0, 10), 'YYYY-MM-DD').format('DD/MM/YYYY') : '-'

// Duas constantes de propósito: a com `g` faz o split (precisa de global), e a SEM `g` testa
// cada pedaço. `RegExp.test` numa regex global carrega `lastIndex` entre chamadas, então testar
// com a mesma instância alternaria true/false a cada item e o link sumiria em metade das linhas.
const URL_SPLIT = /(https?:\/\/\S+)/g
const URL_MATCH = /^https?:\/\//

/**
 * Descrição com o link da fatura clicável.
 *
 * Boa parte das descrições de expense termina com a URL da fatura ("Estorno de pagamento
 * referente a cobrança https://fatura.clientbase.com.br/..."). Como texto puro ela só ocupa
 * espaço; como link levar direto à cobrança que originou o custo.
 *
 * `stopPropagation` no clique: a linha inteira abre o detalhe da expense, e sem isso clicar no
 * link abriria a modal por baixo da aba nova.
 */
const DescriptionWithLinks = ({ text }: { text: string }) => (
  <Typography variant="smLight">
    {text.split(URL_SPLIT).map((part, index) =>
      URL_MATCH.test(part) ? (
        <Box
          key={index}
          component="a"
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{
            color: 'primary.dark',
            textDecoration: 'underline',
            wordBreak: 'break-all'
          }}
        >
          {part}
        </Box>
      ) : (
        part
      )
    )}
  </Typography>
)

/**
 * Lista os itens de uma linha agrupada do extrato de saldo.
 *
 * O extrato colapsa as expenses por tipo numa linha só (SC-10840): antes, uma ação em lote do
 * merchant gerava uma linha por item — 456 num grupo real de produção.
 *
 * NÃO faz chamada nenhuma: os itens vêm prontos no payload do extrato.
 *
 * As colunas são as de fora MENOS as que não existem em despesa: tarifa bancária, valor líquido
 * e método de pagamento são atributos de recebível, e saíam com traço ou repetindo o valor.
 * Sobra "Valor", que é o que o merchant procura aqui.
 */
const ModalBalanceExpenseGroup = ({
  isOpen,
  handleClose,
  title,
  items,
  dateLabel,
  onOpenExpenseDetail
}: Props) => {
  // Mais recente primeiro. O grupo cobre meses, e a data saiu da linha do extrato justamente
  // para viver aqui — então a ordem cronológica é o que dá sentido à lista.
  const sortedItems = [...items].sort((a, b) =>
    (b.expected_on ?? '').localeCompare(a.expected_on ?? '')
  )

  return (
    <Modal
      handleClose={handleClose}
      open={isOpen}
      width="100%"
      maxWidth="1200px"
      maxHeight="90vh"
    >
      <ModalHeader onClose={handleClose}>
        <Stack direction="row" alignItems="center" gap={2}>
          <IconContainer icon="receipt" />
          <Typography variant="mdBold">{title}</Typography>
        </Stack>
      </ModalHeader>

      <ModalContent>
        <EnhancedTable
          mobileCards
          header={[
            { id: 'description', label: 'Descrição', width: '100%' },
            // UMA coluna de data: despesa não tem vencimento nem data de pagamento próprios, só
            // a data do evento que gerou o custo. O rótulo vem da API e nomeia esse evento por
            // tipo ("Data da emissão" na NFS-e, "Data do envio" na notificação), porque
            // "Data prevista" genérico não dizia ao merchant o que aconteceu naquele dia.
            { id: 'expectedOn', label: dateLabel ?? 'Data prevista' },
            { id: 'amount', label: 'Valor' },
            { id: 'actions', label: '' }
          ]}
          rows={sortedItems.map((item) => ({
            uuid: item.uuid,
            clickable: true,
            description: <DescriptionWithLinks text={item.description} />,
            expectedOn: formatDate(item.expected_on),
            amount: (
              <Typography variant="smBold" whiteSpace="nowrap">
                {formatMoney(item.amount)}
              </Typography>
            ),
            actions: (
              <Tooltip
                enterTouchDelay={0}
                arrow
                placement="top"
                title="Detalhes despesa"
              >
                <Box>
                  <ButtonRounded
                    onClick={() => onOpenExpenseDetail(item.uuid)}
                    variant="transparent"
                  >
                    <Icon
                      icon="settings"
                      sx={{ color: theme.palette.neutral[200] }}
                    />
                  </ButtonRounded>
                </Box>
              </Tooltip>
            )
          }))}
          rowAction={(uuid) => onOpenExpenseDetail(uuid)}
          hoverInRow
          hiddePagination
          select={{ useSelect: false }}
        />
      </ModalContent>
    </Modal>
  )
}

export default ModalBalanceExpenseGroup
